import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { getHotelCenterUtils } from '@bingads-webui-campaign/hotel-center-utils';
import { InputControl } from '@bingads-webui/input-control';
import template from './view-template.pug';
import { getBidMultiplierView } from '../single-multiplier-view/view';

export const getLengthOfStayMultipliersView = ({
  i18n, state: componentState, pageContext, multiplierSchema,
}) => {
  const { bidMultiplierUtils } = getHotelCenterUtils({ i18n, state: componentState });
  const { BidMultiplierView } = getBidMultiplierView({ i18n, pageContext, multiplierSchema });

  const {
    serializeBidMultipliers,
    formatStaySegments,
  } = bidMultiplierUtils;

  /**
   * Shared view for Hotel Ads length of stay bid multiplier
   * @extends DeterministicView
   */
  class LengthOfStayMultipliersView extends DeterministicView {
    initialize(options) {
      this.i18n = i18n;
      this.minNumberOfNights = _.isUndefined(options.minNumberOfNights) ?
        1 : options.minNumberOfNights;
      this.maxNumberOfNights = options.maxNumberOfNights || 14;
      this.staySegments = [];
      this.nightsModel = this.initNightsInput('stay-nights-input');
      this.setState({
        staySegments: this.staySegments,
      });
      this.type = options.type || 'lengthOfStay';
    }

    get events() {
      return {
        'click .add-new-query': () => {
          if (this.model.get('isUseParent')) {
            return;
          }
          this.addNewSegment();
        },
        'click .mulitplier-query-remove .filter-query-remove__glyphicon': ({ target }) => {
          if (this.model.get('isUseParent')) {
            return;
          }
          this.removeSegment(target);
        },
      };
    }

    get template() {
      return template;
    }

    initNightsInput(inputView) {
      const schema = {
        name: 'NightsInput',
        type: 'object',
        properties: {
          MinimumNumberOfNights: {
            type: 'integer',
            minimum: this.minNumberOfNights,
            maximum: this.maxNumberOfNights,
          },
        },
      };
      const override = {
        MinimumNumberOfNights: {
          validation: (validators) => {
            const result = validators;

            if (_.has(result, 'decimalLength')) {
              result.decimalLength.message = i18n.getString('HotelCenter_Shared_Validation_Require_Integer');
            }
            return result;
          },
        },
      };
      const NightsModel = backboneSchema(schema, i18n, ['MinimumNumberOfNights', '@odata.type'], null, override);
      const addonText = this.type === 'lengthOfStay' ? i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_InputNights_AddOn_Label') : i18n.getString('HotelCenter_Overview_AdvancedBooking_Multipliers_InputDays_AddOn_Label');

      const nightsModel = new NightsModel();
      const nightsInput = new InputControl({
        entity: nightsModel,
        field: 'MinimumNumberOfNights',
        addonText,
      });

      this.setChild(inputView, nightsInput);

      return nightsModel;
    }

    addNewSegment() {
      const state = this.getChild('stay-nights-input').getState();

      if (!state.isValid) {
        return;
      }
      const minNumberOfNights = this.nightsModel.toJSON().MinimumNumberOfNights;

      if (_.isUndefined(minNumberOfNights)) {
        return;
      }
      this.updateUserInputs();
      this.addSegmentByMinNumberOfNights(minNumberOfNights);
      this.setState({ staySegments: this.staySegments });
    }

    removeSegment(target) {
      this.updateUserInputs();
      this.removeSegmentByMinNumberOfNights(target.title);
      this.setState({ staySegments: this.staySegments });
    }

    addSegmentByMinNumberOfNights(minNumberOfNights) {
      const currentKey = minNumberOfNights.toString();
      const currentIndex = _.findIndex(this.staySegments, it => it.key === currentKey);
      const newSegment = {
        key: currentKey,
        factor: _.noop(),
        strategy: 'increase',
      };

      if (currentIndex > -1) {
        this.staySegments.splice(currentIndex, 1, newSegment);
      } else {
        this.staySegments.push(newSegment);
      }
      this.staySegments = _.sortBy(this.staySegments, segment => Number(segment.key));
      // advanced booking and length of stay share the same view
      const isLengthOfStay = this.type === 'lengthOfStay';

      this.staySegments = formatStaySegments(this.staySegments, isLengthOfStay);
    }

    removeSegmentByMinNumberOfNights(minNumberOfNights) {
      const currentKey = minNumberOfNights.toString();
      const currentSegment = _.findWhere(this.staySegments, { key: currentKey });

      this.staySegments = _.difference(this.staySegments, [currentSegment]);
      // advanced booking and length of stay share the same view
      const isLengthOfStay = this.type === 'lengthOfStay';

      this.staySegments = formatStaySegments(this.staySegments, isLengthOfStay);
    }

    setState({ staySegments, isUseParent }) {
      if (staySegments) {
        _.each(staySegments, (segment) => {
          const bidMultiplier = new BidMultiplierView({
            text: segment.value,
            value: segment.key,
            strategy: segment.strategy,
            factor: segment.factor,
            type: this.type,
          });

          this.setChild(`${segment.key}-bid-multiplier`, bidMultiplier);
        });
        this.staySegments = staySegments;
        this.model.set({ staySegments });
        this.model.trigger('change');
      }
      if (!_.isUndefined(isUseParent)) {
        this.model.set('isUseParent', isUseParent);
        this.getChild('stay-nights-input').setState({ readonly: isUseParent });
        _.each(this.staySegments, (segment) => {
          this.getChild(`${segment.key}-bid-multiplier`).setState({ isUseParent });
        });
      }
      return this;
    }

    updateUserInputs() {
      _.each(this.staySegments, (item) => {
        item.factor = this.getChild(`${item.key}-bid-multiplier`).getState().percentage; // eslint-disable-line
      });
    }

    getState() {
      const lengthOfStayMultipliersState = [];

      _.each(this.staySegments, (item) => {
        lengthOfStayMultipliersState.push(this.getChild(`${item.key}-bid-multiplier`).getState());
      });

      if (_.findWhere(lengthOfStayMultipliersState, { isValid: false })) {
        return {
          isValid: false,
          bidMultipliers: _.noop(),
        };
      }

      const bidMultipliers = serializeBidMultipliers(lengthOfStayMultipliersState);
      const stayFactors = _.pluck(bidMultipliers, 'Factor');
      let i = 0;
      let hasDuplicateMultipliers = false;

      while (i < stayFactors.length - 1) {
        if (stayFactors[i] === stayFactors[i + 1]) {
          hasDuplicateMultipliers = true;
          break;
        } else {
          i += 1;
        }
      }
      if (hasDuplicateMultipliers) {
        return {
          isValid: false,
          bidMultipliers: _.noop(),
          errors: [i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_Duplicate')],
        };
      }

      return {
        isValid: true,
        bidMultipliers,
      };
    }
  }
  return {
    LengthOfStayMultipliersView,
  };
};
